import React, { useState, useMemo, useEffect } from 'react'
import { Card, TextArea, Button } from '@arnsbogroup/react-voicearchive'
import Icon from 'components/Icon'
import classNames from 'classnames'
import { selectUnit } from '@formatjs/intl-utils'
import {
  FormattedMessage,
  FormattedRelativeTime,
  FormattedTime,
  FormattedDate,
} from 'react-intl'
import messages from './messages'
import useApi, { request } from 'hooks/useApi'
import { useMediaPlayer } from 'contexts/PlayerContext'

const ProjectVoiceBook = ({ project }) => {
  const [script, setScript] = useState('')

  const [voiceRequest, loading, errors, refetch, meta] = useApi(
    `/rest/v2/voicerequest/${project.voiceRequestId || voiceRequest?.id || ''}`,
    { load: !!project.voiceRequestId }
  )

  const { playing, play, pause, load } = useMediaPlayer()

  const onPlay = (src) => {
    load({ src, autoplay: true })
    play()
  }

  useEffect(() => {
    if (!voiceRequest?.id) {
      return
    }

    const interval = setInterval(() => {
      refetch(`/rest/v2/voicerequest/${voiceRequest.id}`, {
        method: 'GET',
        body: undefined,
      })
    }, 3000)

    return () => {
      window.clearInterval(interval)
    }
  }, [voiceRequest])

  const voices = useMemo(
    () =>
      Object.values(project.voices || {})
        .map((voice) => {
          if (voiceRequest?.requests) {
            voice.request = voiceRequest.requests.find(
              ({ supplierId }) => supplierId == voice.id
            )
          }

          return voice
        })
        .sort((a, b) => {
          if (
            a?.request?.estimatedDeliveryTimestamp &&
            b?.request?.estimatedDeliveryTimestamp
          ) {
            return a.request.estimatedDeliveryTimestamp >
              b.request.estimatedDeliveryTimestamp
              ? 1
              : 0
            return 1
          } else if (
            a?.request?.estimatedDeliveryTimestamp &&
            !b?.request?.estimatedDeliveryTimestamp
          ) {
            return -1
          } else if (
            !a?.request?.estimatedDeliveryTimestamp &&
            b?.request?.estimatedDeliveryTimestamp
          ) {
            return 1
          } else {
            return 0
          }
        }),
    [project.voices, voiceRequest]
  )

  const onSubmit = () => {
    refetch({
      method: 'POST',
      body: JSON.stringify({
        supplierIds: Object.values(project.voices || {}).map(({ id }) => id),
        script,
        projectId: project.id,
      }),
    })
  }

  return (
    <div className="flex space-x-8">
      <div className="w-1/2 space-y-4">
        <h1 className="text-primary font-medium text-sm space-x-2">
          <Icon icon={['fas', 'circle']} />
          <FormattedMessage {...messages.title} tagName="span" />
        </h1>
        <p>
          <FormattedMessage
            {...messages[voiceRequest?.id ? 'inProgress' : 'subtitle']}
          />
        </p>

        <TextArea
          label="Script"
          rows={5}
          value={voiceRequest?.script || script}
          onChange={({ target: { value } }) => setScript(value)}
          disabled={!!voiceRequest?.script}
          maxLength={280}
        />
        {!voiceRequest?.id && !loading && (
          <Button onClick={onSubmit}>
            <FormattedMessage {...messages.request} />
          </Button>
        )}
      </div>

      <div className="w-1/2">
        <div className="space-y-2 max-h-96 overflow-y-scroll">
          {voices.map((voice, i) => (
            <Card
              key={`voicebook-${voice.id}`}
              className={classNames(
                [
                  'bg-white',
                  'p-1',
                  'pl-2',
                  'flex',
                  'justify-between',
                  'items-center',
                  'font-medium',
                  'border-l-4',
                ],
                {
                  'border-l-positive':
                    i === 0 && voice?.request?.estimatedDeliveryAt,
                  'border-l-blue-400':
                    i !== 0 && !!voice?.request?.estimatedDeliveryAt,
                  'border-l-gray-400 opacity-50':
                    !voice?.request?.estimatedDeliveryAt,
                }
              )}
            >
              <div className="flex">
                <h1>{voice.sku}</h1>
              </div>

              <div className="flex space-x-3 items-center">
                {voice.request ? (
                  <>
                    {voice.request.estimatedDeliveryTimestamp ? (
                      <div className="flex text-xs space-x-2 items-center">
                        <FormattedMessage
                          {...messages.eta}
                          values={{
                            absoluteDate: (
                              <FormattedDate
                                value={voice.request.estimatedDeliveryAt}
                              />
                            ),
                            absoluteTime: (
                              <FormattedTime
                                value={voice.request.estimatedDeliveryAt}
                              />
                            ),
                            relativeTime: (
                              <FormattedRelativeTime
                                numeric="auto"
                                {...selectUnit(
                                  voice.request.estimatedDeliveryTimestamp *
                                    1000
                                )}
                              />
                            ),
                          }}
                          tagName="span"
                        />
                        <i className="w-2 h-2 block rounded-full bg-positive" />

                        {voice.request.sampleUrl && (
                          <Button
                            variant="plain"
                            size="sm"
                            onClick={() =>
                              playing
                                ? pause()
                                : onPlay(voice.request.sampleUrl)
                            }
                          >
                            {playing ? (
                              <>
                                <Icon
                                  icon={['fas', 'pause']}
                                  className="text-primary"
                                />
                                <FormattedMessage
                                  {...messages.pause}
                                  tagName="span"
                                />
                              </>
                            ) : (
                              <>
                                <Icon
                                  icon={['fas', 'play']}
                                  className="text-primary"
                                />
                                <FormattedMessage
                                  {...messages.play}
                                  tagName="span"
                                />
                              </>
                            )}
                          </Button>
                        )}

                        <Button variant={i > 0 ? 'outline' : 'text'} size="sm">
                          <FormattedMessage {...messages.select} />
                        </Button>
                      </div>
                    ) : !voice.request.respondedAt ? (
                      <div className="flex text-xs space-x-2 items-center px-2">
                        <FormattedMessage
                          {...messages.waiting}
                          tagName="span"
                        />
                        <Icon icon="spinner-third" className="animate-spin" />
                      </div>
                    ) : null}
                  </>
                ) : (
                  <i className="w-2 h-2 block rounded-full bg-yellow" />
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  )
}

export default ProjectVoiceBook
